import { Menu } from '@televet/kibble-ui/build/components/Menu';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { downloadPdf } from 'pages/Conversations/utils/files';
import React, { useCallback } from 'react';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useGetNonItemizedReceiptPdfLazyQuery, useGetPaymentReceiptPdfLazyQuery } from 'shared/types/graphql';
import { Box } from '@televet/kibble-ui/build/chakra';

interface DisputeActionMenuProps {
  channelId?: string;
  onConversationClick?: (channelId: string) => void;
  stripePaymentIntentId?: string;
  invoiceId?: string;
}

export const DisputeActionMenu = ({
  channelId,
  onConversationClick,
  stripePaymentIntentId,
  invoiceId,
}: DisputeActionMenuProps): JSX.Element => {
  const toast = useToast();
  const { isInvoicePreviewSupported } = useIntegrationsProvider();
  const hasReceipt = !!stripePaymentIntentId || !!invoiceId;

  const handlePdfDownload = useCallback((base64PDF: string | null | undefined) => {
    const source = `data:application/pdf;base64,${base64PDF}`;
    const fileName = `Receipt_${new Date().toISOString()}.pdf`;
    downloadPdf(source, fileName);
  }, []);

  const [getPaymentReceiptPdf, { loading: isPaymentReceiptLoading }] = useGetPaymentReceiptPdfLazyQuery({
    variables: { data: { invoiceId: invoiceId || '' } },
    onCompleted: (data) => {
      const { base64PDF } = data?.exportItemizedInvoiceAsReceiptPDF;
      handlePdfDownload(base64PDF);
    },
    onError: () => {
      toast({
        title: 'Error downloading receipt.',
        status: 'error',
      });
    },
  });

  const [getNonItemizedPaymentReceiptPdf, { loading: isNonItemizedPaymentReceiptLoading }] =
    useGetNonItemizedReceiptPdfLazyQuery({
      variables: { intentId: stripePaymentIntentId || '' },
      onCompleted: (data) => {
        const { base64PDF } = data?.exportNonItemizedReceiptAsPdf;
        handlePdfDownload(base64PDF);
      },
      onError: () => {
        toast({
          title: 'Error downloading receipt.',
          status: 'error',
        });
      },
    });

  const handleDownloadReceipt = useCallback(() => {
    if (isInvoicePreviewSupported && !!invoiceId) {
      getPaymentReceiptPdf();
    } else if (!!stripePaymentIntentId) {
      getNonItemizedPaymentReceiptPdf();
    }
  }, [
    getNonItemizedPaymentReceiptPdf,
    getPaymentReceiptPdf,
    invoiceId,
    isInvoicePreviewSupported,
    stripePaymentIntentId,
  ]);

  return (
    <Menu
      size="sm"
      buttonProps={{ leftIconName: 'dotsVertical', showRightIcon: false, size: 'sm', variant: 'secondary' }}
      listProps={{
        menuItems: [
          {
            label: (
              <Tooltip label="No conversation associated with this dispute" isDisabled={!!channelId}>
                <Box>
                  <Text size="sm">View Conversation</Text>
                </Box>
              </Tooltip>
            ),
            value: 'view-conversation',
            onSelect: (): void => {
              if (onConversationClick && channelId) {
                onConversationClick(channelId);
              }
            },
            isDisabled: !channelId,
          },
          {
            label: (
              <Tooltip label="No receipt associated with this dispute" isDisabled={hasReceipt}>
                <Box>
                  <Text size="sm">Download Receipt</Text>
                </Box>
              </Tooltip>
            ),
            value: 'download-receipt',
            onSelect: handleDownloadReceipt,
            disabled: !hasReceipt || isPaymentReceiptLoading || isNonItemizedPaymentReceiptLoading,
          },
        ],
      }}
    />
  );
};
